"use client"

import { useCallback, useEffect, useMemo, useState } from "react"

import { usePermissions } from "@/hooks/use-permissions"
import { useAuthStore } from "@/stores/auth-store"
import { hasFlag } from "@/lib/feature-flags"
import {
  getTravelDesk,
  type TravelDeskMeta,
  type TravelDeskRow,
} from "@/lib/travel-desk-api"

/**
 * Travel Desk board state.
 *
 * Owned by a hook rather than the table because the filters live in the page
 * header's action slot and the rows in the panel below — two sibling surfaces,
 * neither of which can hold the state without the other depending on it. Same
 * split `useNeedsBooking` documents for the front-desk board.
 *
 * SELF-GATING: reports `allowed`, and fetches nothing when the clinic lacks the
 * flag or the user lacks `travel_desk.view`. That matters more here than on
 * most boards — the permission is granted per user, so the ordinary case is a
 * coordinator who does NOT have it, and a hook that fetched first and handled
 * the 403 afterwards would put a forbidden request in the network log on every
 * one of their page loads.
 */

/** Booking filter. Static literal keys (T-72-01: never `t(someVar)`). */
export const BOOKING_FILTERS = [
  { key: "all", labelKey: "travelDesk.filters.all" },
  { key: "pending", labelKey: "travelDesk.filters.pending" },
  { key: "booked", labelKey: "travelDesk.filters.booked" },
] as const

export type BookingFilter = (typeof BOOKING_FILTERS)[number]["key"]

/** Window presets, in days forward from today. */
export const RANGE_OPTIONS = [7, 14, 30, 90] as const
export const DEFAULT_RANGE = 30

/** Local `yyyy-MM-dd`. `toISOString()` would shift the day in a west-of-UTC clinic. */
function localDay(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, "0")
  const d = String(date.getDate()).padStart(2, "0")
  return `${y}-${m}-${d}`
}

export interface UseTravelDeskResult {
  allowed: boolean
  isLoading: boolean
  error: boolean
  rows: TravelDeskRow[]
  meta: TravelDeskMeta | null
  range: number
  setRange: (days: number) => void
  booking: BookingFilter
  setBooking: (filter: BookingFilter) => void
  unclaimedOnly: boolean
  setUnclaimedOnly: (value: boolean) => void
  page: number
  setPage: (page: number) => void
  reload: () => void
}

export function useTravelDesk(): UseTravelDeskResult {
  const { isLoaded, can } = usePermissions()
  const featureFlags = useAuthStore((s) => s.user?.feature_flags)

  const allowed =
    isLoaded && can("travel_desk.view") && hasFlag(featureFlags, "travel_desk_enabled")

  const [range, setRangeState] = useState<number>(DEFAULT_RANGE)
  const [booking, setBookingState] = useState<BookingFilter>("all")
  const [unclaimedOnly, setUnclaimedState] = useState(false)
  const [page, setPage] = useState(1)

  const [rows, setRows] = useState<TravelDeskRow[]>([])
  const [meta, setMeta] = useState<TravelDeskMeta | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)

  // Every filter change resets to page 1. Without this, narrowing to "not
  // booked" while on page 3 lands the coordinator on an empty page that looks
  // like an empty board.
  const setRange = useCallback((days: number) => {
    setRangeState(days)
    setPage(1)
  }, [])

  const setBooking = useCallback((filter: BookingFilter) => {
    setBookingState(filter)
    setPage(1)
  }, [])

  const setUnclaimedOnly = useCallback((value: boolean) => {
    setUnclaimedState(value)
    setPage(1)
  }, [])

  const reload = useCallback(() => setReloadKey((k) => k + 1), [])

  const window = useMemo(() => {
    const today = new Date()
    const end = new Date()
    end.setDate(end.getDate() + range)
    return { from: localDay(today), to: localDay(end) }
  }, [range])

  useEffect(() => {
    // No board for this user — fetch nothing. Deliberately NOT resetting state
    // here: a synchronous setState inside an effect triggers a cascading render,
    // and there is nothing to reset anyway because what the hook EXPOSES is
    // masked on `allowed` below. That also covers the revocation case, where
    // `allowed` flips false after a `/me` refetch while stale rows sit in state.
    if (!allowed) return

    let cancelled = false

    // The state writes live inside `load` rather than in the effect body so
    // they land after this tick — a synchronous setState in an effect triggers
    // a cascading render. Same shape `useNeedsBooking` uses.
    const load = async () => {
      setIsLoading(true)
      setError(false)

      try {
        const response = await getTravelDesk({
          from: window.from,
          to: window.to,
          booked: booking === "all" ? undefined : booking === "booked",
          unclaimed: unclaimedOnly,
          page,
        })

        if (!cancelled) {
          setRows(response.data)
          setMeta(response.meta)
        }
      } catch {
        if (!cancelled) {
          setRows([])
          setMeta(null)
          setError(true)
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    load()

    // Guards against a slow first request overwriting a faster second one when
    // the coordinator changes filters twice in quick succession.
    return () => {
      cancelled = true
    }
  }, [allowed, window, booking, unclaimedOnly, page, reloadKey])

  return {
    allowed,
    // Masked on `allowed` rather than cleared in the effect — see the early
    // return above. A user without the board never sees another user's rows,
    // and the hook never writes state it does not have to.
    isLoading: allowed && isLoading,
    error: allowed && error,
    rows: allowed ? rows : [],
    meta: allowed ? meta : null,
    range,
    setRange,
    booking,
    setBooking,
    unclaimedOnly,
    setUnclaimedOnly,
    page,
    setPage,
    reload,
  }
}
